import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState, useCallback } from 'react';
import { FileText, GitBranch, Eye, Settings, ListTodo, ArrowLeft } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ChatInterface } from '@/components/chat/ChatInterface';
import { SpecDocumentEditor } from '@/components/editor/SpecDocumentEditor';
import { WorkflowEditor } from '@/components/workflow/WorkflowEditor';
import { PrototypeViewer } from './PrototypeViewer';
import { TaskBoard } from '@/components/task/TaskBoard';
import { ProjectSettingsModal } from '@/components/modals/ProjectSettingsModal';
import { useProject } from '@/hooks/useProjects';
import { useUIStore } from '@/stores/uiStore';
import { getStatusColor, cn } from '@/lib/utils';

const statusLabels: Record<string, string> = {
  active: '진행 중',
  completed: '완료',
  archived: '보관됨',
  draft: '초안',
};

const MIN_CHAT_WIDTH = 300;
const MAX_CHAT_WIDTH = 600;
const DEFAULT_CHAT_WIDTH = 400;

export function ProjectRoomPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { activeTab, setActiveTab, openModal, setHideSidebar } = useUIStore();
  const {
    project,
    spec,
    workflow,
    prototype,
    isLoading,
    updateProject,
    isUpdatingProject,
    updateSpec,
    updateWorkflow,
    updatePrototype,
  } = useProject(projectId!);

  // Chat panel resizing state
  const [chatPanelWidth, setChatPanelWidth] = useState(DEFAULT_CHAT_WIDTH);
  const isResizing = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Hide sidebar on mount, show on unmount
  useEffect(() => {
    setHideSidebar(true);
    return () => setHideSidebar(false);
  }, [setHideSidebar]);

  // Resize handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizing.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing.current || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const newWidth = e.clientX - containerRect.left;
      const clampedWidth = Math.min(MAX_CHAT_WIDTH, Math.max(MIN_CHAT_WIDTH, newWidth));
      setChatPanelWidth(clampedWidth);
    };

    const handleMouseUp = () => {
      if (isResizing.current) {
        isResizing.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  if (isLoading) {
    return (
      <div className="flex h-screen flex-col">
        <div className="border-b bg-background px-6 py-4">
          <div className="flex items-center gap-4">
            <Skeleton className="h-10 w-10 rounded-lg" />
            <div className="space-y-2">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-72" />
            </div>
          </div>
        </div>
        <div className="flex flex-1">
          <Skeleton className="h-full w-[400px]" />
          <Skeleton className="h-full flex-1" />
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex h-screen flex-col">
        <div className="border-b bg-background px-6 py-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            돌아가기
          </Button>
        </div>
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-semibold">프로젝트를 찾을 수 없습니다</h2>
            <p className="mt-2 text-muted-foreground">
              찾으시는 프로젝트가 존재하지 않습니다.
            </p>
            <Button className="mt-4" onClick={() => navigate('/dashboard')}>
              대시보드로 돌아가기
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col">
      {/* Project Header */}
      <div className="shrink-0 border-b bg-background">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            {/* Back button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              className="shrink-0"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary text-lg font-bold text-primary-foreground">
              {project.name.charAt(0)}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="truncate text-xl font-semibold">{project.name}</h1>
                <Badge className={cn('shrink-0', getStatusColor(project.status))}>
                  {statusLabels[project.status] || project.status}
                </Badge>
              </div>
              <p className="truncate text-sm text-muted-foreground">{project.description}</p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => openModal('projectSettings')}
            >
              <Settings className="mr-2 h-4 w-4" />
              설정
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content: Chat Panel (left) + Tabs (right) */}
      <div ref={containerRef} className="flex flex-1 overflow-hidden">
        {/* Chat Panel (Fixed left) */}
        <div
          className="flex h-full shrink-0 flex-col border-r"
          style={{ width: chatPanelWidth }}
        >
          <ChatInterface projectId={projectId} className="h-full" />
        </div>

        {/* Resize Handle */}
        <div
          className="group relative h-full w-1 shrink-0 cursor-col-resize bg-border transition-colors hover:bg-primary/50"
          onMouseDown={handleMouseDown}
        >
          <div className="absolute inset-y-0 -left-1 -right-1" />
        </div>

        {/* Right Panel: Tabs */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Tab Navigation */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex h-full flex-col">
            <div className="shrink-0 border-b px-4">
              <TabsList className="h-12 w-full justify-start rounded-none border-b-0 bg-transparent p-0">
                <TabsTrigger
                  value="tasks"
                  className="relative h-12 rounded-none border-b-2 border-transparent px-4 pb-3 pt-2 font-medium text-muted-foreground data-[state=active]:border-primary data-[state=active]:text-foreground"
                >
                  <ListTodo className="mr-2 h-4 w-4" />
                  태스크
                </TabsTrigger>
                <TabsTrigger
                  value="spec"
                  className="relative h-12 rounded-none border-b-2 border-transparent px-4 pb-3 pt-2 font-medium text-muted-foreground data-[state=active]:border-primary data-[state=active]:text-foreground"
                >
                  <FileText className="mr-2 h-4 w-4" />
                  스펙 문서
                </TabsTrigger>
                <TabsTrigger
                  value="workflow"
                  className="relative h-12 rounded-none border-b-2 border-transparent px-4 pb-3 pt-2 font-medium text-muted-foreground data-[state=active]:border-primary data-[state=active]:text-foreground"
                >
                  <GitBranch className="mr-2 h-4 w-4" />
                  워크플로우
                </TabsTrigger>
                <TabsTrigger
                  value="prototype"
                  className="relative h-12 rounded-none border-b-2 border-transparent px-4 pb-3 pt-2 font-medium text-muted-foreground data-[state=active]:border-primary data-[state=active]:text-foreground"
                >
                  <Eye className="mr-2 h-4 w-4" />
                  프로토타입
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-hidden">
              <TabsContent value="tasks" className="m-0 h-full">
                <TaskBoard projectId={projectId!} />
              </TabsContent>

              <TabsContent value="spec" className="m-0 h-full">
                <SpecDocumentEditor
                  projectId={projectId!}
                  document={spec ?? null}
                  onSave={updateSpec}
                />
              </TabsContent>

              <TabsContent value="workflow" className="m-0 h-full">
                <WorkflowEditor
                  workflow={workflow ?? null}
                  onSave={updateWorkflow}
                />
              </TabsContent>

              <TabsContent value="prototype" className="m-0 h-full">
                <PrototypeViewer
                  prototype={prototype ?? null}
                  projectId={projectId}
                  projectName={project?.name}
                  onSave={async (code) => {
                    await updatePrototype({ content: code });
                  }}
                />
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </div>

      {/* Project Settings Modal */}
      <ProjectSettingsModal
        project={project}
        onSave={updateProject}
        isUpdating={isUpdatingProject}
      />
    </div>
  );
}
